import { Typography, Divider, Stack } from '@mui/material';

export default function PrivacyPolicyEn() {
  return (
    <Stack spacing={3}>
      <Typography variant="h4" fontWeight={700}>
        BRITESTAGE – Privacy Policy
      </Typography>
      <Typography variant="body2" color="text.secondary">
        <strong>Version 1.0</strong>
        <br />
        Last updated: 13th April 2026
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        1. General Information
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        1.1 Controller
      </Typography>
      <Typography variant="body1">
        The controller within the meaning of the General Data Protection Regulation (GDPR) is:
      </Typography>
      <Typography variant="body1">
        <strong>21Minds GmbH</strong>
        <br />
        Botnanger Str. 6
        <br />
        70193 Stuttgart
        <br />
        Germany
        <br />
        (&quot;BRITESTAGE&quot;, &quot;we&quot;, &quot;us&quot;)
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        1.2 Scope of this Privacy Policy
      </Typography>
      <Typography variant="body1">
        This Privacy Policy informs you about the processing of personal data in connection with:
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>the use of the BRITESTAGE platform,</li>
          <li>the creation and management of user accounts,</li>
          <li>participation in events and collaborations,</li>
          <li>marketplace interactions between users and organizations,</li>
          <li>communication and support services, and</li>
          <li>all related online services.</li>
        </ul>
      </Typography>
      <Typography variant="body1">
        It applies to all users of the platform, regardless of whether they act as:
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>individual users,</li>
          <li>contributors (e.g. speakers, panelists),</li>
          <li>members of organizations, or</li>
          <li>event organizers.</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        1.3 Legal Framework
      </Typography>
      <Typography variant="body1">Processing of personal data is carried out in compliance with:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>the General Data Protection Regulation (GDPR),</li>
          <li>applicable national data protection laws, and</li>
          <li>other relevant legal provisions.</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        1.4 Definitions
      </Typography>
      <Typography variant="body1">
        &quot;Personal data&quot; means any information relating to an identified or identifiable natural person.
      </Typography>
      <Typography variant="body1">
        &quot;Processing&quot; means any operation performed on personal data, such as collection, storage, use,
        disclosure or deletion.
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        2. Data Protection Roles and Responsibilities
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        2.1 BRITESTAGE as Controller
      </Typography>
      <Typography variant="body1">
        BRITESTAGE acts as an <strong>independent controller</strong> where it determines the purposes and means of
        processing, in particular for:
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>account registration and management,</li>
          <li>platform operation and security,</li>
          <li>profile visibility and ranking systems,</li>
          <li>marketplace features,</li>
          <li>billing and subscription management, and</li>
          <li>AI-assisted platform features.</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        2.2 BRITESTAGE as Processor
      </Typography>
      <Typography variant="body1">
        Where users act on behalf of organizations (e.g. event organizers), BRITESTAGE may process personal data{' '}
        <strong>on behalf of such organizations</strong>.
      </Typography>
      <Typography variant="body1">In such cases:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>the organization acts as controller,</li>
          <li>BRITESTAGE acts as processor,</li>
          <li>
            processing is governed by a <strong>Data Processing Agreement (Art. 28 GDPR)</strong>.
          </li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        2.3 Independent Controllers
      </Typography>
      <Typography variant="body1">
        Certain third parties (e.g. payment providers) act as <strong>independent controllers</strong>. Their processing
        is governed by their own privacy policies.
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        2.4 No Joint Controllership
      </Typography>
      <Typography variant="body1">
        Unless explicitly stated otherwise, BRITESTAGE does not act as a joint controller with users or organizations.
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        3. Categories of Personal Data
      </Typography>
      <Typography variant="body1">
        Depending on the use of the platform, we process the following categories of personal data:
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        3.1 Account and Profile Data
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Name, email address, contact details</li>
          <li>Profile information (professional background, skills, biography)</li>
          <li>Profile images and uploaded content</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        3.2 Authentication Data
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Login credentials (processed via authentication providers)</li>
          <li>Session data and authentication tokens</li>
          <li>Login history and device information</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        3.3 Event and Contribution Data
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Event participation data</li>
          <li>Contributions (e.g. sessions, materials, comments)</li>
          <li>Collaboration data within events</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        3.4 Marketplace and Transaction Data
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Booking and engagement data</li>
          <li>Contract-related information between users and organizations</li>
          <li>Payment-related metadata</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        3.5 Technical and Usage Data
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>IP addresses</li>
          <li>Log data</li>
          <li>Device and browser information</li>
          <li>Usage patterns and interactions</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        3.6 Communication Data
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Support requests</li>
          <li>Messages exchanged via the platform</li>
          <li>Notifications and correspondence</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        3.7 AI-Generated and Processed Data
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Inputs provided to AI-assisted features</li>
          <li>Generated summaries, recommendations and content</li>
          <li>Interaction data used to improve functionality</li>
        </ul>
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        4. Purposes of Processing and Legal Bases
      </Typography>
      <Typography variant="body1">We process personal data for the following purposes:</Typography>

      <Typography variant="h6" fontWeight={600}>
        4.1 Platform Provision and Operation
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Provision of user accounts and platform features</li>
          <li>Event management and collaboration</li>
        </ul>
      </Typography>
      <Typography variant="body1">
        <strong>Legal basis:</strong> Art. 6(1)(b) GDPR (performance of a contract)
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        4.2 Platform Security and Integrity
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Prevention of fraud and abuse</li>
          <li>Protection of systems and users</li>
        </ul>
      </Typography>
      <Typography variant="body1">
        <strong>Legal basis:</strong> Art. 6(1)(f) GDPR (legitimate interest)
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        4.3 Profile Visibility and Professional Networking
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Display of user profiles</li>
          <li>Matching between contributors and organizers</li>
        </ul>
      </Typography>
      <Typography variant="body1">
        <strong>Legal basis:</strong> Art. 6(1)(b) GDPR; Art. 6(1)(a) GDPR (where visibility is optional)
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        4.4 Ratings and Ranking Systems
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Display and calculation of ratings</li>
          <li>Matching and recommendation algorithms</li>
        </ul>
      </Typography>
      <Typography variant="body1">
        <strong>Legal basis:</strong> Art. 6(1)(f) GDPR (legitimate interest in platform functionality)
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        4.5 Marketplace and Transactions
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Facilitation of engagements</li>
          <li>Processing of payments</li>
        </ul>
      </Typography>
      <Typography variant="body1">
        <strong>Legal basis:</strong> Art. 6(1)(b) GDPR
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        4.6 AI-Assisted Features
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Content generation and recommendations</li>
          <li>Event support functionalities</li>
        </ul>
      </Typography>
      <Typography variant="body1">
        <strong>Legal basis:</strong> Art. 6(1)(b) GDPR; Art. 6(1)(f) GDPR
      </Typography>
      <Typography variant="body1">No automated decision-making with legal effect takes place.</Typography>

      <Typography variant="h6" fontWeight={600}>
        4.7 Communication and Support
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Responding to inquiries</li>
          <li>Providing support</li>
        </ul>
      </Typography>
      <Typography variant="body1">
        <strong>Legal basis:</strong> Art. 6(1)(b) GDPR; Art. 6(1)(f) GDPR
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        4.8 Legal Compliance
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Fulfillment of legal obligations</li>
          <li>Record keeping</li>
        </ul>
      </Typography>
      <Typography variant="body1">
        <strong>Legal basis:</strong> Art. 6(1)(c) GDPR
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        5. Data Sources
      </Typography>
      <Typography variant="body1">We collect personal data from the following sources:</Typography>

      <Typography variant="h6" fontWeight={600}>
        5.1 Data Provided by Users
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Registration data</li>
          <li>Profile information</li>
          <li>Uploaded content</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        5.2 Data Generated Through Use
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Usage data</li>
          <li>Interaction data</li>
          <li>Event participation data</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        5.3 Data from Organizations
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Data provided by event organizers or employers</li>
          <li>Contributor assignments</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        5.4 Data from Third Parties
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Authentication providers</li>
          <li>Payment providers</li>
          <li>Technical service providers</li>
        </ul>
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        6. Data Retention
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        6.1 General Retention Principles
      </Typography>
      <Typography variant="body1">Personal data is stored only for as long as necessary to:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>fulfill the purposes outlined in this Privacy Policy,</li>
          <li>comply with legal obligations,</li>
          <li>resolve disputes and enforce agreements.</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        6.2 Account Data
      </Typography>
      <Typography variant="body1">
        User account data is retained for the duration of the account. Upon deletion:
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>data is deleted within a maximum period of three (3) months,</li>
          <li>unless legal retention obligations apply.</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        6.3 Event and Contribution Data
      </Typography>
      <Typography variant="body1">
        Certain data (e.g. contributions to events) may remain within event contexts even after account deletion, where
        necessary for:
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>integrity of event documentation,</li>
          <li>legitimate interests of event organizers.</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        6.4 Legal Retention Obligations
      </Typography>
      <Typography variant="body1">Data may be retained longer where required by law, including:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>commercial and tax law obligations,</li>
          <li>audit and compliance requirements.</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        6.5 Technical Data
      </Typography>
      <Typography variant="body1">Log data is retained for limited periods necessary for:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>security,</li>
          <li>troubleshooting,</li>
          <li>system optimization.</li>
        </ul>
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        7. Data Processing by Third Parties
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        7.1 General Information
      </Typography>
      <Typography variant="body1">
        In order to provide, operate, secure and improve the BRITESTAGE platform, we rely on selected third-party
        service providers.
      </Typography>
      <Typography variant="body1">Depending on the context of processing, such third parties may act as:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>
            <strong>Processors (Art. 28 GDPR)</strong>, where they process personal data strictly on our behalf and
            under our instructions, or
          </li>
          <li>
            <strong>Independent Controllers (Art. 4 No. 7 GDPR)</strong>, where they process data for their own
            purposes.
          </li>
        </ul>
      </Typography>
      <Typography variant="body1">Where service providers act as processors, we ensure that:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>
            a <strong>Data Processing Agreement (Art. 28 GDPR)</strong> is concluded,
          </li>
          <li>
            processing is limited to <strong>documented instructions</strong>,
          </li>
          <li>
            appropriate <strong>technical and organizational measures (TOMs)</strong> are implemented, and
          </li>
          <li>confidentiality and security obligations are contractually guaranteed.</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        7.2 Subprocessors
      </Typography>
      <Typography variant="body1">
        To provide and operate the platform, BRITESTAGE engages carefully selected third-party service providers
        (&quot;Subprocessors&quot;) who process personal data on our behalf.
      </Typography>
      <Typography variant="body1">
        Where such processing qualifies as processing on behalf, Subprocessors are engaged in accordance with{' '}
        <strong>Art. 28 GDPR</strong> and are contractually bound to:
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>process personal data only on documented instructions,</li>
          <li>ensure confidentiality,</li>
          <li>implement appropriate technical and organizational measures, and</li>
          <li>support BRITESTAGE in fulfilling its legal obligations.</li>
        </ul>
      </Typography>

      <Typography variant="body1">The current Subprocessors used by BRITESTAGE are listed below:</Typography>

      <Typography variant="subtitle1" fontWeight={600}>
        Microsoft Azure (Hosting &amp; Infrastructure)
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>
            <strong>Provider:</strong> Microsoft Ireland Operations Ltd., Dublin, Ireland
          </li>
          <li>
            <strong>Service:</strong> Cloud infrastructure (hosting, storage, networking)
          </li>
          <li>
            <strong>Data Processed:</strong> All platform data, including user data, content, and technical logs
          </li>
          <li>
            <strong>Purpose:</strong> Hosting and operation of the platform
          </li>
          <li>
            <strong>Processing Location:</strong> European Union
          </li>
          <li>
            <strong>Safeguards:</strong> Encryption, access controls, ISO-certified infrastructure
          </li>
        </ul>
      </Typography>

      <Typography variant="subtitle1" fontWeight={600}>
        MongoDB Atlas (Database Hosting)
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>
            <strong>Provider:</strong> MongoDB, Inc. (EU entity: MongoDB Limited, Ireland)
          </li>
          <li>
            <strong>Service:</strong> Managed database services
          </li>
          <li>
            <strong>Data Processed:</strong> Structured application data (accounts, profiles, event data, content)
          </li>
          <li>
            <strong>Purpose:</strong> Data storage and retrieval
          </li>
          <li>
            <strong>Processing Location:</strong> European Union
          </li>
          <li>
            <strong>Safeguards:</strong> Encryption, role-based access, audit logging
          </li>
        </ul>
      </Typography>

      <Typography variant="subtitle1" fontWeight={600}>
        Clerk (Authentication &amp; Identity Management)
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>
            <strong>Provider:</strong> Clerk, Inc., United States
          </li>
          <li>
            <strong>Service:</strong> Authentication, session and identity management
          </li>
          <li>
            <strong>Data Processed:</strong> Login credentials, email addresses, session data, authentication metadata
          </li>
          <li>
            <strong>Purpose:</strong> Secure user authentication and account protection
          </li>
          <li>
            <strong>Processing Location:</strong> United States
          </li>
          <li>
            <strong>Safeguards:</strong> Encryption, secure token handling, access restrictions
          </li>
        </ul>
      </Typography>

      <Typography variant="subtitle1" fontWeight={600}>
        Lexware (Invoicing &amp; Accounting)
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>
            <strong>Provider:</strong> Haufe-Lexware GmbH &amp; Co. KG, Munzinger Straße 9, 79111 Freiburg, Germany
          </li>
          <li>
            <strong>Service:</strong> Invoicing, accounting and financial management software
          </li>
          <li>
            <strong>Data Processed:</strong> Billing data (names, addresses, email addresses), transaction data
            (invoices, payment status, amounts), tax-relevant information
          </li>
          <li>
            <strong>Purpose:</strong> Creation and management of invoices, accounting and financial reporting,
            compliance with tax and commercial law obligations
          </li>
          <li>
            <strong>Processing Location:</strong> Germany / European Union
          </li>
          <li>
            <strong>Safeguards:</strong> Access controls and authentication, data encryption where applicable,
            compliance with German accounting and data protection standards
          </li>
        </ul>
      </Typography>

      <Typography variant="subtitle1" fontWeight={600}>
        LiveKit (Real-Time Video and Audio Communication)
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>
            <strong>Provider:</strong> LiveKit, Inc., 2261 Market Street #4098, San Francisco, CA 94114, USA
          </li>
          <li>
            <strong>Service:</strong> Real-time communication infrastructure for video, audio and data streaming
          </li>
          <li>
            <strong>Data Processed:</strong> Audio and video streams, real-time communication metadata (timestamps,
            session identifiers), technical data (IP addresses, connection data)
          </li>
          <li>
            <strong>Purpose:</strong> Enabling live video and audio transmissions during events, supporting interactive
            communication features
          </li>
          <li>
            <strong>Processing Location:</strong> European Union and global infrastructure
          </li>
          <li>
            <strong>Transfer Mechanism:</strong> EU Standard Contractual Clauses (SCCs)
          </li>
          <li>
            <strong>Safeguards:</strong> Transport encryption, secure transmission protocols, access restrictions and
            session-based isolation
          </li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        7.3 Third Parties Acting as Independent Controllers
      </Typography>
      <Typography variant="body1">
        Certain services integrated into the platform are provided by third parties who process personal data for their
        own purposes and act as independent controllers. In such cases, BRITESTAGE has no control over the processing
        activities of these providers.
      </Typography>

      <Typography variant="subtitle1" fontWeight={600}>
        Mollie (Payment Processing)
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>
            <strong>Provider:</strong> Mollie B.V., Amsterdam, Netherlands
          </li>
          <li>
            <strong>Service:</strong> Payment processing
          </li>
          <li>
            <strong>Data Processed:</strong> Payment and billing data, transaction metadata, limited personal data
          </li>
          <li>
            <strong>Purpose:</strong> Execution of payments, fraud prevention, legal compliance
          </li>
          <li>
            <strong>Role:</strong> Independent controller
          </li>
        </ul>
      </Typography>
      <Typography variant="body1">Further information can be found in Mollie&apos;s privacy policy.</Typography>

      <Typography variant="h6" fontWeight={600}>
        7.4 International Data Transfers
      </Typography>
      <Typography variant="body1">
        Where personal data is transferred to countries outside the European Economic Area (EEA), BRITESTAGE ensures
        that such transfers comply with <strong>Art. 44 et seq. GDPR</strong>.
      </Typography>
      <Typography variant="body1">In particular, we rely on the following safeguards:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>EU Standard Contractual Clauses (SCCs),</li>
          <li>Additional technical and organizational measures,</li>
          <li>Adequacy decisions where applicable (e.g. EU-U.S. Data Privacy Framework).</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        7.5 Changes to Subprocessors
      </Typography>
      <Typography variant="body1">
        BRITESTAGE may update or replace Subprocessors from time to time where necessary for operational, technical or
        legal reasons. Where required by applicable law, users will be informed of material changes in advance.
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        7.6 Right to Object to Subprocessors (Enterprise Provision)
      </Typography>
      <Typography variant="body1">
        Where BRITESTAGE processes personal data on behalf of organizations, such organizations may have the right to
        object to the engagement of new Subprocessors.
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>BRITESTAGE will provide prior notice (generally at least 15 days).</li>
          <li>Customers may object on reasonable data protection grounds.</li>
          <li>
            In case of justified objection, BRITESTAGE will provide an alternative where feasible, or allow termination
            of the affected services.
          </li>
        </ul>
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        8. Data Sharing and Disclosure
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        8.1 General Principles
      </Typography>
      <Typography variant="body1">Personal data is only disclosed to third parties where:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>it is necessary for the provision of the platform,</li>
          <li>there is a legal obligation to do so, or</li>
          <li>the user has provided consent.</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        8.2 Disclosure to Organizations
      </Typography>
      <Typography variant="body1">Where users participate in events or act on behalf of organizations:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>relevant personal data may be shared with the respective organization,</li>
          <li>this includes profile data, contribution data, and event-related information,</li>
          <li>the organization acts as an independent controller for such processing.</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        8.3 Disclosure to Other Users
      </Typography>
      <Typography variant="body1">
        Depending on platform functionality and visibility settings, personal data may be visible to other users,
        including:
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>profile information,</li>
          <li>contributions and event participation,</li>
          <li>ratings and feedback.</li>
        </ul>
      </Typography>
      <Typography variant="body1">Visibility is controlled by user settings where applicable.</Typography>

      <Typography variant="h6" fontWeight={600}>
        8.4 Disclosure to Service Providers
      </Typography>
      <Typography variant="body1">
        Personal data is shared with service providers in accordance with Section 7 (Subprocessors).
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        8.5 Disclosure for Legal Reasons
      </Typography>
      <Typography variant="body1">We may disclose personal data where required to:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>comply with legal obligations,</li>
          <li>enforce legal claims,</li>
          <li>prevent fraud or misuse,</li>
          <li>protect the rights, property or safety of users or third parties.</li>
        </ul>
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        9. User Rights (Data Subject Rights)
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        9.1 Overview
      </Typography>
      <Typography variant="body1">Users have the following rights under the GDPR:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Right of access (Art. 15 GDPR)</li>
          <li>Right to rectification (Art. 16 GDPR)</li>
          <li>Right to erasure (Art. 17 GDPR)</li>
          <li>Right to restriction of processing (Art. 18 GDPR)</li>
          <li>Right to data portability (Art. 20 GDPR)</li>
          <li>Right to object (Art. 21 GDPR)</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        9.2 Right of Access
      </Typography>
      <Typography variant="body1">
        Users may request confirmation as to whether personal data is being processed and obtain a copy of such data.
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        9.3 Right to Rectification
      </Typography>
      <Typography variant="body1">Users may request correction of inaccurate or incomplete data.</Typography>

      <Typography variant="h6" fontWeight={600}>
        9.4 Right to Erasure
      </Typography>
      <Typography variant="body1">Users may request deletion of their data where:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>the data is no longer necessary,</li>
          <li>consent is withdrawn,</li>
          <li>processing is unlawful, or</li>
          <li>legal obligations require deletion.</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        9.5 Right to Restriction
      </Typography>
      <Typography variant="body1">Users may request restriction of processing under certain conditions.</Typography>

      <Typography variant="h6" fontWeight={600}>
        9.6 Right to Data Portability
      </Typography>
      <Typography variant="body1">
        Users may request to receive their data in a structured, commonly used format.
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        9.7 Right to Object
      </Typography>
      <Typography variant="body1">
        Users may object to processing based on legitimate interests. In such cases, processing will cease unless
        compelling legitimate grounds exist.
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        9.8 Exercising Rights
      </Typography>
      <Typography variant="body1">Requests can be submitted via the contact details provided in Section 14.</Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        10. Right to Withdraw Consent
      </Typography>
      <Typography variant="body1">
        Where processing is based on consent (Art. 6(1)(a) GDPR), users have the right to withdraw consent at any time.
        Withdrawal does not affect the lawfulness of processing prior to withdrawal.
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        11. Automated Decision-Making
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        11.1 No Automated Decisions with Legal Effect
      </Typography>
      <Typography variant="body1">
        BRITESTAGE does not carry out automated decision-making within the meaning of Art. 22 GDPR that produces legal
        effects or similarly significant effects.
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        11.2 AI-Assisted Features
      </Typography>
      <Typography variant="body1">The platform uses AI-assisted systems to:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>generate recommendations,</li>
          <li>support event management,</li>
          <li>assist in content creation.</li>
        </ul>
      </Typography>
      <Typography variant="body1">These systems:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>do not replace human decision-making,</li>
          <li>do not produce binding outcomes,</li>
          <li>serve only as support tools.</li>
        </ul>
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        12. Cookies and Tracking Technologies
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        12.1 General Information
      </Typography>
      <Typography variant="body1">
        BRITESTAGE uses cookies and similar technologies to ensure the functionality and security of the platform.
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        12.2 Types of Cookies
      </Typography>
      <Typography variant="body1">
        <strong>Essential Cookies:</strong> Required for platform operation; enable authentication and session
        management.
      </Typography>
      <Typography variant="body1">
        <strong>Functional Cookies:</strong> Improve usability; store preferences.
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        12.3 No Marketing Tracking (Current State)
      </Typography>
      <Typography variant="body1">At present, BRITESTAGE does not use:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>third-party advertising cookies,</li>
          <li>cross-site tracking technologies.</li>
        </ul>
      </Typography>
      <Typography variant="body1">
        This may change in the future and will be reflected in this Privacy Policy.
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        12.4 Legal Basis
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Essential cookies: Art. 6(1)(b) GDPR</li>
          <li>Optional cookies: Art. 6(1)(a) GDPR (where applicable)</li>
        </ul>
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        13. Data Security
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        13.1 General Measures
      </Typography>
      <Typography variant="body1">
        BRITESTAGE implements appropriate technical and organizational measures to protect personal data.
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        13.2 Security Measures Include
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Encryption in transit and at rest</li>
          <li>Access controls and authentication mechanisms</li>
          <li>Secure cloud infrastructure</li>
          <li>Monitoring and logging</li>
        </ul>
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        13.3 No Absolute Security
      </Typography>
      <Typography variant="body1">
        While we take reasonable measures, no system can guarantee absolute security.
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        14. Contact and Supervisory Authority
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        14.1 Contact
      </Typography>
      <Typography variant="body1">For all data protection inquiries, please contact:</Typography>
      <Typography variant="body1">
        21Minds GmbH
        <br />
        Botnanger Str. 6
        <br />
        70193 Stuttgart
        <br />
        Germany
        <br />
        Email: privacy@britestage.com
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        14.2 Data Protection Officer
      </Typography>
      <Typography variant="body1">
        A Data Protection Officer has not been appointed where not legally required. If appointed, contact details will
        be published here.
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        14.3 Supervisory Authority
      </Typography>
      <Typography variant="body1">
        Users have the right to lodge a complaint with a supervisory authority. The competent authority in Germany is
        typically:
      </Typography>
      <Typography variant="body1">
        Landesbeauftragter für den Datenschutz und die Informationsfreiheit Baden-Württemberg
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        14.4 Alternative Authorities
      </Typography>
      <Typography variant="body1">
        Users may also contact the supervisory authority in their place of residence within the EU.
      </Typography>
    </Stack>
  );
}
